/**
 * Safe localStorage utility that handles SecurityError and other access issues
 * This is necessary when localStorage access is denied (e.g., in sandboxed iframes,
 * with browser security settings, or in certain browser contexts)
 */

/**
 * Check if localStorage is available and accessible
 */
export const isLocalStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
      return false;
    }
    
    // Test if we can actually access localStorage
    const testKey = '__localStorage_test__';
    window.localStorage.setItem(testKey, 'test');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    // SecurityError, QuotaExceededError, or other errors
    return false;
  }
};

/**
 * Safely get an item from localStorage
 * @param key - The key to retrieve
 * @returns The value if found, null otherwise
 */
export const safeGetItem = (key: string): string | null => {
  try {
    if (!isLocalStorageAvailable()) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch (error) {
    console.warn(`[localStorage] Failed to get item "${key}":`, error);
    return null;
  }
};

/**
 * Safely set an item in localStorage
 * @param key - The key to set
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export const safeSetItem = (key: string, value: string): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`[localStorage] Failed to set item "${key}":`, error);
    return false;
  }
};

/**
 * Safely remove an item from localStorage
 * @param key - The key to remove
 * @returns true if successful, false otherwise
 */
export const safeRemoveItem = (key: string): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[localStorage] Failed to remove item "${key}":`, error);
    return false;
  }
};

/**
 * Safely clear all items from localStorage
 * @returns true if successful, false otherwise
 */
export const safeClear = (): boolean => {
  try {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    window.localStorage.clear();
    return true;
  } catch (error) {
    console.warn(`[localStorage] Failed to clear:`, error);
    return false;
  }
};

/**
 * Safely get and parse a JSON item from localStorage
 * @param key - The key to retrieve
 * @param defaultValue - Default value if parsing fails or item doesn't exist
 * @returns The parsed value or defaultValue
 */
export const safeGetJSON = <T>(key: string, defaultValue: T): T => {
  try {
    const item = safeGetItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`[localStorage] Failed to parse JSON for "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely set a JSON item in localStorage
 * @param key - The key to set
 * @param value - The value to store (will be JSON stringified)
 * @returns true if successful, false otherwise
 */
export const safeSetJSON = (key: string, value: any): boolean => {
  try {
    const jsonString = JSON.stringify(value);
    return safeSetItem(key, jsonString);
  } catch (error) {
    console.warn(`[localStorage] Failed to stringify JSON for "${key}":`, error);
    return false;
  }
};

/**
 * Check if sessionStorage is available and accessible
 */
export const isSessionStorageAvailable = (): boolean => {
  try {
    if (typeof window === 'undefined' || typeof window.sessionStorage === 'undefined') {
      return false;
    }
    
    // Test if we can actually access sessionStorage
    const testKey = '__sessionStorage_test__';
    window.sessionStorage.setItem(testKey, 'test');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch (error) {
    // SecurityError, QuotaExceededError, or other errors
    return false;
  }
};

/**
 * Safely get an item from sessionStorage
 * @param key - The key to retrieve
 * @returns The value if found, null otherwise
 */
export const safeSessionGetItem = (key: string): string | null => {
  try {
    if (!isSessionStorageAvailable()) {
      return null;
    }
    return window.sessionStorage.getItem(key);
  } catch (error) {
    console.warn(`[sessionStorage] Failed to get item "${key}":`, error);
    return null;
  }
};

/**
 * Safely set an item in sessionStorage
 * @param key - The key to set
 * @param value - The value to store
 * @returns true if successful, false otherwise
 */
export const safeSessionSetItem = (key: string, value: string): boolean => {
  try {
    if (!isSessionStorageAvailable()) {
      return false;
    }
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`[sessionStorage] Failed to set item "${key}":`, error);
    return false;
  }
};

/**
 * Safely remove an item from sessionStorage
 * @param key - The key to remove
 * @returns true if successful, false otherwise
 */
export const safeSessionRemoveItem = (key: string): boolean => {
  try {
    if (!isSessionStorageAvailable()) {
      return false;
    }
    window.sessionStorage.removeItem(key);
    return true;
  } catch (error) {
    console.warn(`[sessionStorage] Failed to remove item "${key}":`, error);
    return false;
  }
};

/**
 * Safely clear all items from sessionStorage
 * @returns true if successful, false otherwise
 */
export const safeSessionClear = (): boolean => {
  try {
    if (!isSessionStorageAvailable()) {
      return false;
    }
    window.sessionStorage.clear();
    return true;
  } catch (error) {
    console.warn(`[sessionStorage] Failed to clear:`, error);
    return false;
  }
};

/**
 * Safely get and parse a JSON item from sessionStorage
 * @param key - The key to retrieve
 * @param defaultValue - Default value if parsing fails or item doesn't exist
 * @returns The parsed value or defaultValue
 */
export const safeSessionGetJSON = <T>(key: string, defaultValue: T): T => {
  try {
    const item = safeSessionGetItem(key);
    if (item === null) {
      return defaultValue;
    }
    return JSON.parse(item) as T;
  } catch (error) {
    console.warn(`[sessionStorage] Failed to parse JSON for "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Safely set a JSON item in sessionStorage
 * @param key - The key to set
 * @param value - The value to store (will be JSON stringified)
 * @returns true if successful, false otherwise
 */
export const safeSessionSetJSON = (key: string, value: any): boolean => {
  try {
    const jsonString = JSON.stringify(value);
    return safeSessionSetItem(key, jsonString);
  } catch (error) {
    console.warn(`[sessionStorage] Failed to stringify JSON for "${key}":`, error);
    return false;
  }
};

